import React from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { Form } from 'formik';
import { TFunction } from 'i18next';
import {
  FormRadioButtons,
  FormDateField,
  FormTextField,
  FormikWrapper,
} from '@hvcgroep/design-system';

import { RegistrationDeskStepComponent } from '../../types/multistep-form';
import JourneyActionButtons from '../../components/JourneyActionButtons';
import gtm from '../../../../shared/helpers/gtm';
import { APP_ID } from '../../../../shared/config/constants';
import trackValidationError from '../../../../shared/helpers/trackValidationError';

const PersonalDetailsForm: RegistrationDeskStepComponent<'proceed.savePersonalDetails'> = (props) => {
  const {
    onProceed,
    context,
    onGoBack,
  } = props;

  const initialValues = {
    salutation: context?.salutation || '' as 'sir' | 'madam',
    firstName: context?.firstName || '',
    infix: context?.infix || '',
    lastName: context?.lastName || '',
    dateOfBirth: context?.dateOfBirth ? new Date(context.dateOfBirth).toISOString().split('T')[0] : '',
    email: context?.email || '',
    tel: context?.tel || '',
  };

  const { t } = useTranslation();

  const ageValidation = new Date();
  ageValidation.setFullYear(ageValidation.getFullYear() - 18);
  ageValidation.setHours(0, 0, 0, 0);

  const validationSchema = Yup.object().shape({
    salutation: Yup.string().required(t('registration_desk.screens.personal_details.salutation.error.required')).oneOf(['sir', 'madam'] as const).defined(),
    firstName: Yup.string().required(t('registration_desk.screens.personal_details.first_name.error.required')),
    infix: Yup.string(),
    lastName: Yup.string().required(t('registration_desk.screens.personal_details.last_name.error.required')),
    dateOfBirth: Yup.date().required(t('registration_desk.screens.personal_details.date_of_birth.error.required')).max(ageValidation, t('registration_desk.screens.personal_details.date_of_birth.error.age_validation')),
    email: Yup.string().email(t('registration_desk.screens.personal_details.email.error.format')).required(t('registration_desk.screens.personal_details.email.error.required')),
    tel: Yup.string()
      .required(t('registration_desk.screens.personal_details.tel.error.required'))
      .transform(formatTel).test('tel', t('registration_desk.screens.personal_details.tel.error.format'), (value: any) => validateTel(value, t)),
  });

  return (
    <div>
      <FormikWrapper
        validationSchema={validationSchema}
        onSubmit={(values) => {
          const parsedValues = {
            ...values,
            dateOfBirth: new Date(values.dateOfBirth),
            tel: formatTel(values.tel),
          };

          onProceed(parsedValues);
        }}
        initialValues={initialValues}
        validateOnBlur={false}
        validateOnChange={false}
        onValidationError={(errors) => {
          trackValidationError(String(APP_ID), 'PersonalDetailsForm', errors);
        }}
      >
        {({
          values,
        }) => {
          return (
            <Form noValidate>
              <FormRadioButtons
                name="salutation"
                label={t('registration_desk.screens.personal_details.salutation.label')}
                isLiveValidationDisabled
                options={[
                  { key: 'sir', value: t('registration_desk.screens.personal_details.salutation.options.sir') },
                  { key: 'madam', value: t('registration_desk.screens.personal_details.salutation.options.madam') },
                ]}
              />
              <FormTextField
                name="firstName"
                label={t('registration_desk.screens.personal_details.first_name.label')}
                placeholder={t('registration_desk.screens.personal_details.first_name.placeholder')}
                isLiveValidationDisabled
              />
              <FormTextField
                name="infix"
                label={t('registration_desk.screens.personal_details.infix.label')}
                placeholder={t('registration_desk.screens.personal_details.infix.placeholder')}
                isLiveValidationDisabled
              />
              <FormTextField
                name="lastName"
                label={t('registration_desk.screens.personal_details.last_name.label')}
                placeholder={t('registration_desk.screens.personal_details.last_name.placeholder')}
                isLiveValidationDisabled
              />
              <FormDateField
                name="dateOfBirth"
                label={t('registration_desk.screens.personal_details.date_of_birth.label')}
                isLiveValidationDisabled
              />
              <FormTextField
                name="email"
                label={t('registration_desk.screens.personal_details.email.label')}
                placeholder={t('registration_desk.screens.personal_details.email.placeholder')}
                isLiveValidationDisabled
              />
              <FormTextField
                name="tel"
                label={t('registration_desk.screens.personal_details.tel.label')}
                placeholder={t('registration_desk.screens.personal_details.tel.placeholder')}
                isLiveValidationDisabled
              />

              <JourneyActionButtons
                onGoBack={() => {
                  if (!onGoBack) return;

                  const parsedValues = {
                    ...values,
                    dateOfBirth: values.dateOfBirth ? new Date(values.dateOfBirth) : null as unknown as Date,
                  };

                  onGoBack(parsedValues);
                }}
              />
            </Form>
          );
        }}
      </FormikWrapper>
    </div>
  );
};

function formatTel(tel: string) {
  if (!tel) {
    return '';
  }

  const cleanTel = tel
    .replace(/[^+0-9]/g, '')
    .replace(/^\+31/, '0')
    .replace(/\s/g, '');

  return cleanTel;
}

function validateTel(value: string | undefined, t: TFunction) {
  if (!value) {
    return false;
  }

  value = value
    .replace(/\s/g, '');

  if (!/^\d{10}$/.test(value) || /[a-zA-Z]/.test(value)) {
    // @todo: once the tracking of ALL validation events is in place, this should be removed.
    gtm.trackUserEvent(String(APP_ID), 'Failed form validation', t('registration_desk.screens.personal_details.tel.label'));
    return false;
  }

  return true;
}


export default PersonalDetailsForm;
